// Lightweight imperative toast system.
// Usage: import { toast } from "./Toast"; then toast("message") or toast.error("oops").
// Mount <ToastContainer /> once near the root of the tree.

import React, { useEffect, useState, useCallback } from "react";

const listeners = new Set();
let seq = 0;

function emit(message, variant) {
  const id = ++seq;
  listeners.forEach(l => l({ id, message: String(message || ""), variant }));
  return id;
}

export const toast = Object.assign(
  (msg) => emit(msg, "info"),
  {
    success: (msg) => emit(msg, "success"),
    error: (msg) => emit(msg, "error"),
    info: (msg) => emit(msg, "info"),
  }
);

const COLORS = {
  info: { bg: "#1E40AF", fg: "#fff" },
  success: { bg: "#15803d", fg: "#fff" },
  error: { bg: "#b91c1c", fg: "#fff" },
};

export function ToastContainer() {
  const [items, setItems] = useState([]);

  const remove = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  // Two-phase dismissal: mark the toast `leaving` to play the exit animation, then unmount it.
  const dismiss = useCallback((id) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, leaving: true } : i)));
    setTimeout(() => remove(id), 200);
  }, [remove]);

  useEffect(() => {
    const onToast = (item) => {
      setItems(prev => [...prev, item]);
      const timeout = item.variant === "error" ? 6000 : 3500;
      setTimeout(() => dismiss(item.id), timeout);
    };
    listeners.add(onToast);
    return () => listeners.delete(onToast);
  }, [dismiss]);

  if (items.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        pointerEvents: "none",
      }}
    >
      {items.map(item => {
        const c = COLORS[item.variant] || COLORS.info;
        return (
          <div
            key={item.id}
            role="status"
            aria-live="polite"
            onClick={() => dismiss(item.id)}
            style={{
              background: c.bg,
              color: c.fg,
              padding: "10px 16px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              maxWidth: 360,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              cursor: "pointer",
              pointerEvents: "auto",
              fontFamily: "'Geist', sans-serif",
              lineHeight: 1.4,
              animation: item.leaving
                ? "pitcherly-toast-out 200ms ease-in forwards"
                : "pitcherly-toast-in 200ms ease-out",
            }}
          >
            {item.message}
          </div>
        );
      })}
      <style>{`
        @keyframes pitcherly-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pitcherly-toast-out {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(16px); }
        }
      `}</style>
    </div>
  );
}
